import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import HeroSection from './components/HeroSection';
import DownloadPrompt from './components/DownloadPrompt';
import HowItWorks from './components/HowItWorks';
import Benefits from './components/Benefits';
import SocialProof from './components/SocialProof';
import CallToAction from './components/CallToAction';
import ScrollProgress from './components/ScrollProgress';
import './App.css';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from './styles/theme';
import { GlobalStyles } from './styles/GlobalStyles';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LoginScreen } from './components/LoginScreen';
import RecipeResultScreen from './components/RecipeResultScreen';
import ChefCarousel from './components/ChefCarousel';
import DishesGallery from './components/DishesGallery';
import SplashScreen from './components/SplashScreen';
import ChefBuddy from './components/ChefBuddy';
import Food from './components/Food';
import HeaderCustom from './components/Header';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import AppStoreRedirect from './components/AppStoreRedirect';
import PinterestRedirect from './components/PinterestRedirect';
import Analytics from './components/Analytics';

const AppContainer  = styled(motion.div)`
  height: 100%;
  position: relative;
  background: ${({ theme }) => theme.colors.background}; /* Убедимся, что фон соответствует теме */
`;

const ScrollContainer = styled(motion.div)`
  position: relative;
  scroll-behavior: smooth; /* Добавляем плавный скролл */
  scroll-snap-type: y proximity; /* Меняем на proximity для более плавного перехода */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Section = styled(motion.section)`
  min-height: 500px;
  width: 100%;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  // overflow: hidden;
  
  /* Добавляем градиентную нижнюю границу */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100px; /* Высота градиентного перехода */
    background: linear-gradient(to bottom, transparent, ${({ theme }) => theme.colors.background});
    pointer-events: none;
    z-index: 2;
  }
  
  /* Добавляем градиентную верхнюю границу */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100px; /* Высота градиентного перехода */
    background: linear-gradient(to top, transparent, ${({ theme }) => theme.colors.background});
    pointer-events: none;
    z-index: 2;
  }
`;

// Особый стиль для первой секции (Hero)
const HeroSectionDiv = styled(motion.section)`
  min-height: 800px !important;
  width: 100%;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  overflow: hidden;
  margin-top: -env(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);
  
  /* Черный фон в начальном состоянии */
  background: #000;
`;

// Особый стиль для последней секции (CTA)
const CTASection = styled(Section)`
min-height: 900px
  /* Только верхний градиент для последней секции */
  &::after {
    display: none;
  }
`;

// Анимации для секций
const sectionVariants = {
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  hidden: { opacity: 0, y: 50, transition: { duration: 0.8 } }
};

// Специальная секция для компонента DownloadPrompt с улучшенным размытием
const DownloadSection = styled(motion.section)`
  height: 50vh;
  min-height: 700px;
  width: 100%;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  // &::before {
  //   content: '';
  //   position: absolute;
  //   top: -1px;
  //   left: 0;
  //   right: 0;
  //   height: 150px;
  //   background: linear-gradient(to bottom, 
  //     ${({ theme }) => theme.colors.background} 0%, 
  //     rgba(10, 10, 10, 0.6) 50%,
  //     transparent 100%);
  //   z-index: 10;
  
  //   pointer-events: none;
  // }
  
  // &::after {
  //   content: '';
  //   position: absolute;
  //   bottom: -1px;
  //   left: 0;
  //   right: 0;
  //   height: 150px;
  //   background: linear-gradient(to top, 
  //     ${({ theme }) => theme.colors.background} 0%, 
  //     rgba(10, 10, 10, 0.6) 50%,
  //     transparent 100%);
  //   z-index: 10;
  //   // backdrop-filter: blur(8px);
  //   // -webkit-backdrop-filter: blur(3px);
  //   pointer-events: none;
  // }
`;

// Создаем компонент Header
const Header = styled(motion.header)`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const IconButton = styled(motion.button)`
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DownloadButton = styled(motion.button)`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 25px;
  padding: 8px 20px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

// Создаем отдельный компонент MainContent для основного содержимого
const MainContent = ({ onVideoLoaded }) => {
  const containerRef = useRef(null);
  const sections = ["hero", "download-prompt", "how-it-works", "benefits", "social-proof", "cta", "dishes-gallery"];

  return (
    <AppContainer>
      <ScrollContainer ref={containerRef}>
        <HeroSectionDiv 
          id="hero"
          initial={{ backgroundColor: "#000000" }}
          animate={{ backgroundColor: "#000000" }}
          variants={{
            hidden: { opacity: 1 },
            visible: { opacity: 1 }
          }}
        >
          <HeroSection onVideoLoaded={onVideoLoaded} />
        </HeroSectionDiv>
        
         <DownloadSection 
          id="download-prompt"
          initial="visible"
          // whileInView="hu"
          // viewport={{ once: false, amount: 0.4 }}
          variants={sectionVariants}
        >
          <DownloadPrompt />
        </DownloadSection>
        
        {/* Средние секции */}
        <Section id="how-it-works">
          <HowItWorks />
        </Section>
        <Section id="benefits">
          <ChefCarousel />
        </Section>
        <Section id="social-proof">
          <SocialProof />
        </Section>
        
        {/* Последняя секция (CTA) */}
        <CTASection id="cta">
          <CallToAction />
        </CTASection>
        
        {/* Новая секция с карточками блюд */}
        <Section id="dishes-gallery">
          <DishesGallery />
        </Section>
      </ScrollContainer>
      
       <DownloadSection 
        id="download-prompt-footer"
      >
        <DownloadPrompt />
      </DownloadSection>
    </AppContainer>
  );
};

// Создаем обертку для home роута, чтобы добавить хедер
const HomeRoute = () => {
  // Состояние для управления видимостью хедера
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // Функция для получения события о загрузке видео от HeroSection
  const handleVideoLoaded = () => {
    setVideoLoaded(true);
  };

  return (
    <>
      <HeaderCustom isVisible={videoLoaded} />
      <MainContent onVideoLoaded={handleVideoLoaded} />
    </>
  );
};

// Создаем основной компонент для маршрутизации
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/home" element={<HomeRoute />} />
      <Route path="/try-chef" element={<ChefBuddy />} />
      <Route path="/food" element={<Food />} />
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/tk" element={<AppStoreRedirect />} />
      <Route path="/download" element={<AppStoreRedirect />} />
      <Route path="/app" element={<AppStoreRedirect />} />
      <Route path="/pinterest" element={<PinterestRedirect />} />
      <Route path="/pin" element={<PinterestRedirect />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/stats" element={<Analytics />} />
      <Route path="/" element={<Navigate to="/tk" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <GlobalStyles />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}

export default App;
